import { Dispatch } from 'umi';

import { LeadTaskModel, TaskAction } from '@/typings/models/LeadTask';
import { TaskAutomationConsequenceType } from '@/components/TaskAutomation/constants';
import { fetchSingleLeadTaskRequest } from '@/services/api/leadTask';
import { getTaskAssignee, getTaskLead } from '@/utils/task';

/**
 * The server treats the open_* consequence types as no-ops — the task UI is
 * expected to open the matching surface after the action call succeeds:
 *  - open_add_comment    → task drawer with the comment composer pre-filled
 *  - open_compose_email  → mailto: draft to the lead's customer (subject/text)
 *  - open_appointment    → the create-appointment drawer for the task's lead
 *
 * The same placeholders the automation form documents are substituted:
 * {firstname} {lastname} {email} {phone} {assignee}.
 */

export const substitutePlaceholders = (text: string, task: LeadTaskModel): string => {
  const customer = (getTaskLead(task) as any)?.customer || {};
  const assignee = getTaskAssignee(task);
  const assigneeName = (assignee as any)?.fullname
    || [assignee?.firstname, assignee?.lastname].filter(Boolean).join(' ');

  return String(text)
    .replaceAll('{firstname}', customer.firstname || '')
    .replaceAll('{lastname}', customer.lastname || '')
    .replaceAll('{email}', customer.email || '')
    .replaceAll('{phone}', customer.phone || '')
    .replaceAll('{assignee}', assigneeName || '');
};

/** mailto: draft to the task's lead customer — null without an email address */
export const buildMailtoUrl = (
  task: LeadTaskModel,
  meta: { subject?: string; text?: string },
): string | null => {
  const email = (getTaskLead(task) as any)?.customer?.email;
  if (!email) return null;
  const query = [
    meta.subject ? `subject=${encodeURIComponent(substitutePlaceholders(meta.subject, task))}` : null,
    meta.text ? `body=${encodeURIComponent(substitutePlaceholders(meta.text, task))}` : null,
  ].filter(Boolean).join('&');

  return `mailto:${email}${query ? `?${query}` : ''}`;
};

/**
 * Runs the UI-handled consequences of an executed task action.
 * Returns true when something was opened (popovers close themselves on it).
 */
export function applyTaskUiConsequences(
  dispatch: Dispatch,
  task: LeadTaskModel,
  action?: TaskAction | null,
): boolean {
  const consequences = (action?.consequences || []).filter(Boolean);
  if (consequences.length === 0) return false;

  const lead = getTaskLead(task);
  let opened = false;

  consequences.forEach((consequence: any) => {
    const meta = consequence?.meta || {};
    switch (consequence?.type) {
      case TaskAutomationConsequenceType.OpenAddComment: {
        // The task drawer owns the lead's comment composer — open it (a
        // re-open on the same task is a harmless refresh) and pre-fill.
        dispatch({ type: 'leadTasks/openTaskDetailsDrawer', payload: { task } });
        dispatch({
          type: 'leadTasks/setCommentPrefill',
          payload: { text: substitutePlaceholders(meta.comment || '', task), at: Date.now() },
        });
        opened = true;
        break;
      }
      case TaskAutomationConsequenceType.OpenComposeEmail: {
        // No in-app composer exists — email leaves via the user's mail client,
        // same as every mailto link in the app.
        const mailto = buildMailtoUrl(task, meta);
        if (!mailto) break;
        window.location.href = mailto;
        opened = true;
        break;
      }
      case TaskAutomationConsequenceType.OpenAppointment: {
        dispatch({
          type: 'serviceRequest/toggleCreateServiceRequestDrawer',
          payload: {
            openAppointmentDetails: true,
            ...(lead ? { lead } : {}),
          },
        });
        opened = true;
        break;
      }
      default:
        break;
    }
  });

  return opened;
}

/**
 * Like applyTaskUiConsequences, but tolerant of list-shaped payloads: the
 * minimal action shape carries no `consequences`, so when they are missing
 * the single-task detail is fetched once and the matching action (and its
 * richer task, incl. the full lead) is used instead. Call sites await this
 * after the action POST succeeded.
 */
export async function runTaskUiConsequences(
  dispatch: Dispatch,
  task: LeadTaskModel,
  action?: TaskAction | null,
): Promise<boolean> {
  if (!action) return false;

  let resolvedTask = task;
  let resolvedAction: TaskAction = action;

  if (!action.consequences) {
    const { response, data } = (await fetchSingleLeadTaskRequest({
      params: { taskId: task.id },
    })) as { response?: Response; data?: any };
    const detail = data?.data;
    if (!response?.ok || !detail) return false;
    resolvedTask = detail;
    resolvedAction = (detail.actions || []).find(
      (candidate: TaskAction) => candidate.uuid === action.uuid,
    ) || action;
  }

  return applyTaskUiConsequences(dispatch, resolvedTask, resolvedAction);
}
